import {
    BLOCK_DRAWING,
    BLOCK_EMBED,
    BLOCK_FILE,
    BLOCK_IMAGE,
    BLOCK_PARAGRAPH,
    canInsertBlockIn,
    contentRef,
    embed,
    INLINE_LINK,
    moveToNextLeafBlock,
} from '@gitbook/doc-core';
import { Inline, TextRange } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';
import { isHttpUrl, normalizeUrl } from '@gitbook/util-url';

import { isEnter, isShiftEnter } from '../common';

const REFS_INTO_CONTENT_REF_BLOCK = ['collection', 'space', 'page', 'anchor', 'file'];

/**
 * When pressing <Enter> after a paragraph composed of only a url,
 * we transform that paragrah in an embed.
 */
export const insertPlugin: Plugin = {
    onKeyDown: (event, change) => {
        const { value } = change;

        if (!value.isCollapsed || !isEnter(event)) {
            return;
        }

        const { startBlock, selection } = value;

        if (!TextRange.isTextRange(selection)) {
            return;
        }

        if (
            !startBlock ||
            startBlock.type !== BLOCK_PARAGRAPH ||
            !selection.isAtEndOf(startBlock)
        ) {
            return;
        }

        const { text } = startBlock;

        if (!isHttpUrl(text)) {
            return;
        }

        // If embeds are not allowed in this block, we keep the url as it is
        const parent = value.document.getParent(startBlock.key);
        if (!parent || !canInsertBlockIn(parent, BLOCK_EMBED)) {
            return;
        }

        const inlineLinkWithRef = startBlock.nodes.find(
            (node) =>
                Inline.isInline(node) &&
                node.type === INLINE_LINK &&
                node.data.get('ref') &&
                REFS_INTO_CONTENT_REF_BLOCK.includes(node.data.get('ref').kind)
        ) as Inline;

        // When the url points to a content ref instead of an external/random url,
        // create a content ref block instead of an embed.
        if (inlineLinkWithRef) {
            const contentRefBlock = contentRef.createContentRefBlock(
                inlineLinkWithRef.data.get('ref')
            );
            change
                .replaceNodeByKey(startBlock.key, contentRefBlock)
                .collapseToStartOf(contentRefBlock);
            return;
        }

        const url = normalizeUrl(text, { sortQueryParameters: false });

        const embedBlock = embed.createEmbedBlock(url);
        change.replaceNodeByKey(startBlock.key, embedBlock).collapseToStartOf(embedBlock);
    },
};

/**
 * Slate plugin to handle interaction in captions:
 *  - Create paragraph after parent block if pressing <Enter>
 */
export const captionPlugin: Plugin = {
    onKeyDown: (event, change) => {
        if (isEnter(event) || isShiftEnter(event)) {
            const { value } = change;
            const { startBlock, document } = value;

            if (!startBlock || startBlock.type !== BLOCK_PARAGRAPH) {
                return;
            }

            // Are we in a caption fragment ?
            const fragment = document.getFragmentParent(startBlock.key);
            if (fragment?.object !== 'fragment') {
                return;
            }
            const fragmentParent = document.getParent(fragment.key);
            if (
                !fragmentParent ||
                !fragmentParent.isBlock() ||
                ![BLOCK_EMBED, BLOCK_IMAGE, BLOCK_FILE, BLOCK_DRAWING].includes(fragmentParent.type)
            ) {
                return;
            }

            // When we are on an image caption we want to get to the upper parent
            // and create an empty paragraph below it.
            const currentBlock =
                fragmentParent.type === BLOCK_IMAGE
                    ? document.getParent(fragmentParent.key)
                    : fragmentParent;

            // @ts-ignore
            moveToNextLeafBlock(change, currentBlock);
            return change;
        }
    },
};
